import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getWorkspaceTokens } from './workspaceStyles';
import {
  buildMobileFrameHeight,
  resolveResponsiveMinHeight,
} from './workspaceResponsiveSizing';
import { ChartPresentationProvider } from '../../charts/common/ChartPresentationContext';

function AnalyticsNivoFrame({
  allowAxisOverflow = false,
  children,
  frameRef,
  minHeight,
  sx,
  style,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const resolvedMinHeight = resolveResponsiveMinHeight(minHeight, tokens.objectMinHeight.chart);
  const frameHeight = buildMobileFrameHeight(resolvedMinHeight, tokens.objectMinHeight.chart);
  const localRef = useRef(null);
  const [size, setSize] = useState({ width: undefined, height: undefined });

  useEffect(() => {
    const node = localRef.current;
    if (!node) return undefined;

    const updateSize = () => {
      const bounds = node.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        setSize({ width: bounds.width, height: bounds.height });
      }
    };
    updateSize();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const presentation = useMemo(() => ({
    mode: 'interactive',
    static: false,
    width: size.width,
    height: size.height,
  }), [size.height, size.width]);

  const setRefs = (node) => {
    localRef.current = node;
    if (typeof frameRef === 'function') frameRef(node);
    else if (frameRef) frameRef.current = node;
  };

  return (
    <ChartPresentationProvider value={presentation}>
      <Box
        ref={setRefs}
        data-testid="analytics-nivo-frame"
        data-axis-overflow={allowAxisOverflow ? 'true' : undefined}
        data-frame-height={JSON.stringify(frameHeight)}
        data-min-height={JSON.stringify(resolvedMinHeight)}
        style={style}
        sx={{
          width: '100%',
          height: frameHeight,
          maxHeight: { xs: 'none', md: '100%' },
          minWidth: 0,
          minHeight: resolvedMinHeight,
          flex: { xs: '0 0 auto', md: '1 1 0' },
          position: 'relative',
          overflow: allowAxisOverflow ? 'visible' : 'hidden',
          '& > *': {
            minWidth: 0,
            height: '100%',
            maxHeight: '100%',
            ...(allowAxisOverflow ? { overflow: 'visible' } : {}),
          },
          ...(allowAxisOverflow ? {
            '& svg': {
              overflow: 'visible',
            },
          } : {}),
          ...sx,
        }}
      >
        {children}
      </Box>
    </ChartPresentationProvider>
  );
}

export default AnalyticsNivoFrame;
